'use client';

import { useCallback, useRef } from 'react';

import type { ClipCard } from '@/lib/reel-studio/agent/blocks';

/**
 * One clip, playable in the chat. The media URLs are same-origin proxy URLs
 * the server built for clips its scoped lookup returned. When the quote has a
 * moment, the player starts there.
 */
export function ClipPlayer({ clip }: { clip: ClipCard }) {
    const videoRef = useRef<HTMLVideoElement>(null);

    const seekToMoment = useCallback(() => {
        const video = videoRef.current;
        if (video && clip.startSec != null && video.currentTime < clip.startSec) {
            video.currentTime = clip.startSec;
        }
    }, [clip.startSec]);

    return (
        <figure className="flex w-full flex-col overflow-hidden rounded-lg border border-default-200 bg-content1">
            <video
                ref={videoRef}
                className="aspect-video w-full bg-black object-contain"
                src={clip.videoUrl}
                poster={clip.thumbnailUrl ?? undefined}
                controls
                playsInline
                preload="none"
                onLoadedMetadata={seekToMoment}
                aria-label={`Video response from respondent ${clip.panelistId}`}
            />
            <figcaption className="flex flex-col gap-1 p-3">
                {clip.quote ? (
                    <p className="text-sm leading-snug text-foreground">“{clip.quote}”</p>
                ) : (
                    <p className="text-sm italic text-default-500">Transcript not available</p>
                )}
                <p className="text-xs text-default-500">
                    Respondent {clip.panelistId}
                    {clip.store ? ` · ${clip.store}` : ''}
                </p>
            </figcaption>
        </figure>
    );
}

export function ClipGrid({ clips }: { clips: ClipCard[] }) {
    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {clips.map((clip) => (
                <ClipPlayer key={clip.clipId} clip={clip} />
            ))}
        </div>
    );
}
